// ============================================================================
// numerarioFormato.js — formatação e cores do submódulo Numerário
// (mesmo papel do trocoFormato.js no Troco: fonte única do visual do módulo)
// ============================================================================

export const COR = '#0d7a74';        // teal do Financeiro
export const COR_CLARA = '#f0fdfa';
export const COR_BORDA = '#99f6e4';

// Semântica de cor das situações. Sobra é azul e falta é vermelha; "incompleto"
// é âmbar porque NÃO é um resultado — é a ausência de um.
export const COR_SOBRA = '#1d4ed8';
export const COR_FALTA = '#b91c1c';
export const COR_EXATO = '#15803d';
export const COR_ALERTA = '#b45309';
export const COR_NEUTRA = '#64748b';

export const dinheiro = (v) => (v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

export const dataBR = (iso) => (iso
    ? String(iso).slice(0, 10).split('-').reverse().join('/')
    : '—');

// Dia da semana abreviado de uma data ISO — usado nos filtros de coleta, onde
// "qua 08/07" comunica muito mais que "08/07": a rede recolhe em três dias
// diferentes e é o dia que distingue um malote do outro.
//
// ⚠️ Lido em UTC de propósito. A data vem do banco como AAAA-MM-DD, e
//    `new Date('2026-07-08')` já é interpretado como UTC — mas os getters
//    LOCAIS puxam para o fuso do Brasil (−03), devolvendo o dia anterior. Com
//    getDay() em vez de getUTCDay(), toda coleta de quarta apareceria como
//    terça e o filtro passaria a mentir sobre o dia da coleta.
const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
export const diaSemana = (iso) => (iso
    ? DIAS_SEMANA[new Date(`${String(iso).slice(0, 10)}T00:00:00Z`).getUTCDay()]
    : '');

// Par rótulo+campo dos filtros. ⚠️ Existe para o par NÃO SE PARTIR quando a
// barra quebra de linha: com os selects soltos num flex-wrap, o rótulo "Coleta"
// ficava numa linha e o campo dele na seguinte, órfão.
export const parFiltro = {
    display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
};

/** "2026-07" a partir de uma data ISO — a chave do filtro de período. */
export const mesDe = (iso) => String(iso).slice(0, 7);

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** "julho/2026" — o rótulo do filtro de período. */
export const rotuloMes = (aaaaMm) => {
    const [ano, mes] = String(aaaaMm).split('-');
    return `${MESES[Number(mes) - 1]}/${ano}`;
};

/**
 * O rótulo de uma semana nos seletores da Conferência e do Lançamento SAP.
 *
 * ⚠️ Mostra as DATAS das coletas, não só a da quarta que dá nome à semana.
 *   "Semana de 15/07" sozinho se lê como "a coleta do dia 15", quando aquela
 *   semana pode reunir três coletas — a terça do MOX, a quarta das nove lojas e
 *   a sexta do MAL. Foi exatamente esse o ponto de confusão relatado na
 *   primeira versão do seletor.
 */
export const rotuloSemana = (s) => {
    const dias = (s.coletas || []).map((c) => dataBR(c.dtRelatorio).slice(0, 5));
    const quando = dias.length > 1 ? ` · coletas ${dias.join(', ')}` : '';
    return `Semana de ${dataBR(s.semana)}${quando} — ${s.guias} loja(s)`;
};

export const percentual = (v) => (v === null || v === undefined
    ? '—'
    : `${Math.round(Number(v) * 100)}%`);

/** Cor de uma diferença em reais. Zero fica neutro para não virar ruído. */
export const corDaDiferenca = (v) => {
    const n = Number(v);
    if (!n) return COR_NEUTRA;
    return n > 0 ? COR_SOBRA : COR_FALTA;
};

/** Cor + rótulo de cada situação devolvida pelo motor. */
export const estiloSituacao = (situacao) => {
    switch (situacao) {
        case 'Exato': return { cor: COR_EXATO, fundo: '#f0fdf4' };
        case 'Sobra': return { cor: COR_SOBRA, fundo: '#eff6ff' };
        case 'Falta': return { cor: COR_FALTA, fundo: '#fef2f2' };
        // Não é resultado — é dado faltando. Âmbar, não vermelho: vermelho aqui
        // leria como problema na loja, e o problema é da carga.
        case 'Declarado incompleto': return { cor: COR_ALERTA, fundo: '#fffbeb' };
        default: return { cor: COR_NEUTRA, fundo: '#f8fafc' };
    }
};

// Estilos de tabela compartilhados pelas telas do módulo.
export const th = {
    textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700,
    color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3,
    borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
};
export const td = {
    padding: '9px 12px', fontSize: 14, borderBottom: '1px solid #f1f5f9', color: '#0f172a',
};
// ⚠️ tabular-nums não é enfeite: sem ele os dígitos têm larguras diferentes e a
//    coluna de dinheiro deixa de alinhar na vírgula.
export const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
